import z from "zod/v4";

// ─── Feed Item Types ─────────────────────────────────────────

export const E_FeedItemType = {
  COMPANY_NEWS: "COMPANY_NEWS",
  MACRO_EVENT: "MACRO_EVENT",
  CALENDAR_UPCOMING: "CALENDAR_UPCOMING",
  TRENDING_STOCK: "TRENDING_STOCK",
  PORTFOLIO_INSIGHT: "PORTFOLIO_INSIGHT",
} as const;

export type E_FeedItemType =
  (typeof E_FeedItemType)[keyof typeof E_FeedItemType];

export const E_FeedItemStatus = {
  PENDING: "PENDING",
  ENRICHED: "ENRICHED",
  FAILED: "FAILED",
} as const;

export type E_FeedItemStatus =
  (typeof E_FeedItemStatus)[keyof typeof E_FeedItemStatus];

export const E_FeedItemSentiment = {
  POSITIVE: "POSITIVE",
  NEGATIVE: "NEGATIVE",
  NEUTRAL: "NEUTRAL",
  MIXED: "MIXED",
} as const;

export type E_FeedItemSentiment =
  (typeof E_FeedItemSentiment)[keyof typeof E_FeedItemSentiment];

export function isFeedItemSentiment(
  sentiment: string,
): sentiment is E_FeedItemSentiment {
  switch (sentiment) {
    case E_FeedItemSentiment.POSITIVE:
    case E_FeedItemSentiment.NEGATIVE:
    case E_FeedItemSentiment.NEUTRAL:
    case E_FeedItemSentiment.MIXED:
      return true;
    default:
      return false;
  }
}

export const E_FeedItemCategory = {
  EARNINGS: "earnings",
  IPO: "ipo",
  FED_DECISION: "fed_decision",
  ANALYST_DOWNGRADE: "analyst_downgrade",
  GEOPOLITICAL: "geopolitical",
  CPI: "cpi",
  JOBS_REPORT: "jobs_report",
  LAWSUIT: "lawsuit",
  REGULATORY: "regulatory",
  ANALYST_UPGRADE: "analyst_upgrade",
  GDP: "gdp",
  EXECUTIVE_CHANGE: "executive_change",
  TRADE_POLICY: "trade_policy",
  SEC_FILING: "sec_filing",
  DIVIDEND: "dividend",
  PRODUCT_LAUNCH: "product_launch",
  PARTNERSHIP: "partnership",
  STOCK_SPLIT: "stock_split",
} as const;

export type T_FeedItemCategory =
  (typeof E_FeedItemCategory)[keyof typeof E_FeedItemCategory];

export function isFeedItemCategory(
  category: string,
): category is T_FeedItemCategory {
  switch (category) {
    case E_FeedItemCategory.EARNINGS:
    case E_FeedItemCategory.IPO:
    case E_FeedItemCategory.FED_DECISION:
    case E_FeedItemCategory.ANALYST_DOWNGRADE:
    case E_FeedItemCategory.GEOPOLITICAL:
    case E_FeedItemCategory.CPI:
    case E_FeedItemCategory.JOBS_REPORT:
    case E_FeedItemCategory.LAWSUIT:
    case E_FeedItemCategory.REGULATORY:
    case E_FeedItemCategory.ANALYST_UPGRADE:
    case E_FeedItemCategory.GDP:
    case E_FeedItemCategory.EXECUTIVE_CHANGE:
    case E_FeedItemCategory.TRADE_POLICY:
    case E_FeedItemCategory.SEC_FILING:
    case E_FeedItemCategory.DIVIDEND:
    case E_FeedItemCategory.PRODUCT_LAUNCH:
    case E_FeedItemCategory.PARTNERSHIP:
    case E_FeedItemCategory.STOCK_SPLIT:
      return true;
    default:
      return false;
  }
}

// ─── Zod Schemas ─────────────────────────────────────────────

export const z_FeedItemResponse = z.object({
  cuid: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  fullDescription: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  imageUrl: z.string().nullable(),
  // Client-facing ticker chips and filters use rankable tickers only.
  // Raw mentioned tickers stay server-side for audit/debug metadata.
  tickers: z.array(z.string()),
  primaryTicker: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  eventDate: z.string().nullable(), // ISO date string from FeedEvent.event_date
  itemType: z.enum(E_FeedItemType),
  category: z.string().nullable(),
  sentiment: z.enum(E_FeedItemSentiment).nullable(),
  topics: z.array(z.string()),
  publishedAt: z.string(), // ISO datetime
  score: z.number(),
});

export type T_FeedItemResponse = z.infer<typeof z_FeedItemResponse>;

// Index into the post-diversity `diverse[]` array. Score-based cursors
// broke once `enforceDiversity` started moving deferred items out of fis
// order — a high-fis item appended to the tail became unreachable when
// the cursor's score dropped below it. An index cursor follows the
// final ordered list directly.
//
// `viewSuppressionBefore` pins the view-suppression query to the state
// at session start. First page sets it server-side (now()); subsequent
// pages pass it back. Views recorded during the current scrolling
// session affect the *next* session, not the current pagination run.
export const z_FeedCursor = z.object({
  idx: z.number().int().min(0),
  viewSuppressionBefore: z.string().datetime().optional(),
});

export type T_FeedCursor = z.infer<typeof z_FeedCursor>;

export const z_FeedPage = z.object({
  items: z.array(z_FeedItemResponse),
  nextCursor: z_FeedCursor.nullable(),
});

export type T_FeedPage = z.infer<typeof z_FeedPage>;

// ─── Enrichment Output (from Claude Sonnet) ─────────────────

export const z_EnrichmentResult = z.object({
  summary: z.string(),
  fullDescription: z.string(),
  sentiment: z.enum(E_FeedItemSentiment),
  topics: z.array(z.string()).max(5),
  impactScore: z.number().min(0).max(1),
  category: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  imageUrl: z.string().nullable(),
});

export type T_EnrichmentResult = z.infer<typeof z_EnrichmentResult>;

// ─── Calendar Title Derivation ───────────────────────────────

// Maps each category to the descriptor used when building a calendar
// event title. `includeTicker` is false for macro events that aren't
// tied to a single company (FOMC, CPI, jobs, GDP, trade, geopolitics).
export const CATEGORY_TITLE_LABEL: Record<
  T_FeedItemCategory,
  { label: string; includeTicker: boolean }
> = {
  [E_FeedItemCategory.EARNINGS]: { label: "earnings", includeTicker: true },
  [E_FeedItemCategory.IPO]: { label: "IPO", includeTicker: true },
  [E_FeedItemCategory.FED_DECISION]: {
    label: "FOMC meeting",
    includeTicker: false,
  },
  [E_FeedItemCategory.CPI]: { label: "CPI release", includeTicker: false },
  [E_FeedItemCategory.JOBS_REPORT]: {
    label: "jobs report",
    includeTicker: false,
  },
  [E_FeedItemCategory.GDP]: { label: "GDP release", includeTicker: false },
  [E_FeedItemCategory.TRADE_POLICY]: {
    label: "trade policy",
    includeTicker: false,
  },
  [E_FeedItemCategory.GEOPOLITICAL]: {
    label: "geopolitical event",
    includeTicker: false,
  },
  [E_FeedItemCategory.ANALYST_DOWNGRADE]: {
    label: "downgrade",
    includeTicker: true,
  },
  [E_FeedItemCategory.ANALYST_UPGRADE]: {
    label: "upgrade",
    includeTicker: true,
  },
  [E_FeedItemCategory.DIVIDEND]: { label: "dividend", includeTicker: true },
  [E_FeedItemCategory.EXECUTIVE_CHANGE]: {
    label: "exec change",
    includeTicker: true,
  },
  [E_FeedItemCategory.LAWSUIT]: { label: "lawsuit", includeTicker: true },
  [E_FeedItemCategory.REGULATORY]: {
    label: "regulatory event",
    includeTicker: true,
  },
  [E_FeedItemCategory.SEC_FILING]: {
    label: "SEC filing",
    includeTicker: true,
  },
  [E_FeedItemCategory.PRODUCT_LAUNCH]: {
    label: "product launch",
    includeTicker: true,
  },
  [E_FeedItemCategory.PARTNERSHIP]: {
    label: "partnership",
    includeTicker: true,
  },
  [E_FeedItemCategory.STOCK_SPLIT]: {
    label: "stock split",
    includeTicker: true,
  },
};

// Builds a generic, descriptive title for a calendar event derived from
// a feed item — e.g. "NVDA earnings on May 20", "CPI release on May 13".
// Never uses the LLM-written headline (item.title), so the saved title
// stays neutral and machine-derived. Falls back to "Event[ on <date>]"
// when category/ticker are missing.
export function buildFeedCalendarTitle(
  item: Pick<T_FeedItemResponse, "primaryTicker" | "category" | "eventDate"> & {
    displaySubject?: string | null;
  },
): string {
  const descriptor =
    item.category && isFeedItemCategory(item.category)
      ? CATEGORY_TITLE_LABEL[item.category]
      : null;
  const subject = item.displaySubject?.trim() || item.primaryTicker;

  const base = descriptor
    ? descriptor.includeTicker && subject
      ? `${subject} ${descriptor.label}`
      : descriptor.label
    : subject
      ? subject
      : "Event";

  if (!item.eventDate) return base;
  const d = new Date(item.eventDate);
  if (Number.isNaN(d.getTime())) return base;
  const dateLabel = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${base} on ${dateLabel}`;
}

// ─── Calendar Unification Key ────────────────────────────────

// Returns a stable `${TICKER}|${YYYY-MM-DD}` key used to unify calendar
// state across all feed items that reference the same ticker on the
// same calendar day. Returns null when either component is missing so
// the caller can fall back to per-item-cuid identity.
export function buildCalendarUnificationKey(
  primaryTicker: string | null | undefined,
  eventDate: string | Date | null | undefined,
): string | null {
  if (!primaryTicker || !eventDate) return null;
  const d = eventDate instanceof Date ? eventDate : new Date(eventDate);
  if (Number.isNaN(d.getTime())) return null;
  // Date-only, UTC-normalized. The original ISO string carries a time
  // zone, but for "saved on this day" semantics we collapse to the UTC
  // calendar day, which matches how the backend stores event_date.
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${primaryTicker}|${yyyy}-${mm}-${dd}`;
}

// ─── Default Impact Scores by Category ───────────────────────

export const DEFAULT_IMPACT_SCORES: Record<T_FeedItemCategory, number> = {
  [E_FeedItemCategory.EARNINGS]: 1.0,
  [E_FeedItemCategory.IPO]: 0.9,
  [E_FeedItemCategory.FED_DECISION]: 1.0,
  [E_FeedItemCategory.ANALYST_DOWNGRADE]: 0.9,
  [E_FeedItemCategory.GEOPOLITICAL]: 0.9,
  [E_FeedItemCategory.CPI]: 0.9,
  [E_FeedItemCategory.JOBS_REPORT]: 0.85,
  [E_FeedItemCategory.LAWSUIT]: 0.8,
  [E_FeedItemCategory.REGULATORY]: 0.8,
  [E_FeedItemCategory.ANALYST_UPGRADE]: 0.8,
  [E_FeedItemCategory.GDP]: 0.8,
  [E_FeedItemCategory.EXECUTIVE_CHANGE]: 0.7,
  [E_FeedItemCategory.TRADE_POLICY]: 0.7,
  [E_FeedItemCategory.SEC_FILING]: 0.6,
  [E_FeedItemCategory.DIVIDEND]: 0.6,
  [E_FeedItemCategory.PRODUCT_LAUNCH]: 0.5,
  [E_FeedItemCategory.PARTNERSHIP]: 0.4,
  [E_FeedItemCategory.STOCK_SPLIT]: 0.3,
};
